import { BsCheckCircleFill, BsCircle } from 'react-icons/bs';

import * as S from './PasswordStrengthIndicator.styled';
import useTranslate from 'hooks/useTranslate';
import {
    getPasswordCriteria,
    getPasswordStrength,
    PasswordStrength,
} from 'utils/validations/password';

interface Props {
    password: string;
    /**
     * Optional language override (e.g. the reset page derives it from the
     * email link). Falls back to the user's configured language.
     */
    lng?: string;
}

const TOTAL_SEGMENTS = 4;

const STRENGTH_META: Record<
    Exclude<PasswordStrength, 'empty'>,
    { segments: number; color: string; labelKey: string }
> = {
    weak: { segments: 1, color: '#EF4444', labelKey: 'passwordStrengthWeak' },
    fair: { segments: 2, color: '#F59E0B', labelKey: 'passwordStrengthFair' },
    good: { segments: 3, color: '#3B82F6', labelKey: 'passwordStrengthGood' },
    strong: { segments: 4, color: '#22C55E', labelKey: 'passwordStrengthStrong' },
};

export default function PasswordStrengthIndicator({ password, lng }: Props) {
    const { translate } = useTranslate(lng);

    if (!password) return null;

    const criteria = getPasswordCriteria(password);
    const strength = getPasswordStrength(password);
    const meta = strength === 'empty' ? STRENGTH_META.weak : STRENGTH_META[strength];

    const requirements = [
        { key: 'passwordReqLength', met: criteria.length },
        { key: 'passwordReqLowercase', met: criteria.lowercase },
        { key: 'passwordReqUppercase', met: criteria.uppercase },
        { key: 'passwordReqNumber', met: criteria.number },
        { key: 'passwordReqSymbol', met: criteria.symbol },
    ];

    return (
        <S.Wrapper aria-live="polite">
            <S.BarRow role="meter" aria-label={translate('passwordStrength')}>
                <S.Segments>
                    {Array.from({ length: TOTAL_SEGMENTS }).map((_, index) => (
                        <S.Segment key={index} active={index < meta.segments} color={meta.color} />
                    ))}
                </S.Segments>
                <S.StrengthLabel color={meta.color}>{translate(meta.labelKey)}</S.StrengthLabel>
            </S.BarRow>

            <S.CriteriaList>
                {requirements.map((requirement) => (
                    <S.Criterion key={requirement.key} met={requirement.met}>
                        <S.IconWrap met={requirement.met}>
                            {requirement.met ? <BsCheckCircleFill /> : <BsCircle />}
                        </S.IconWrap>
                        {translate(requirement.key)}
                    </S.Criterion>
                ))}
            </S.CriteriaList>
        </S.Wrapper>
    );
}
